<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useDowntimes } from '@/composables/eos/downtime'
import { SiteType } from '@/types/core/Site'
import type { IInfrastructureDowntimeSummaryFilters } from '@/types/eos/filters'
import { tableColumnPercentageWithTwoDecimals } from '@/util/formatters'
import { Icon } from '@iconify/vue'
import { DateTime } from 'luxon'
import { reactive, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = defineProps<{
  startDate: string
  endDate: string
}>()

const filters = reactive<IInfrastructureDowntimeSummaryFilters>({
  site_id: undefined,
  name: undefined,
  start_datetime: props.startDate || DateTime.now().startOf('month').toISO(),
  end_datetime: props.endDate || DateTime.now().endOf('month').toISO(),
})

// Watch for prop changes and update URL
watch(
  () => [props.startDate, props.endDate],
  ([newStartDate, newEndDate]) => {
    if (newStartDate && newEndDate) {
      filters.start_datetime = newStartDate
      filters.end_datetime = newEndDate

      // Update URL query parameters
      router.replace({
        query: {
          ...router.currentRoute.value.query,
          start_date: newStartDate,
          end_date: newEndDate,
        },
      })
    }
  }
)

const { getRacksDowntimeSummaryQuery } = useDowntimes()

const { isFetching, isError, isRefetching, isPending, data, error, refetch } = getRacksDowntimeSummaryQuery(filters)

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex gap-2">
      <RcSiteSelect
        placeholder="Site ID or Name"
        :type="SiteType.DataCenter"
        :disabled="isPending"
        style="width: 200px"
        @update:model-value="onSiteFilterUpdate"
      />
      <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
    </div>
    <el-table v-loading="isFetching" :data="data" empty-text="No data" stripe>
      <el-table-column prop="name" label="Rack Name" :min-width="196" />
      <el-table-column
        prop="period_uptime_percentage"
        label="Period Uptime"
        :min-width="196"
        :formatter="tableColumnPercentageWithTwoDecimals"
      />
    </el-table>
  </template>
</template>
